import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORIES, MENU } from "../data/menu";
import { ChapterHeader, Reveal } from "./Reveal";

const PHOTOS = [
  { src: "/images/karadjordjeva.jpg", label: "Karađorđeva" },
  { src: "/images/rostilj.jpg", label: "Sveže meso" },
  { src: "/images/porcija.jpg", label: "Porcija kuće" },
];

export const MenuSection = () => {
  const [active, setActive] = useState("lepinja");

  return (
    <section id="meni" className="relative py-28 sm:py-40 bg-white" data-testid="menu-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <ChapterHeader
          number="02"
          title={<>Meni</>}
          sub="Cene su u dinarima, odluka je teška, a kajanje ne postoji. Uz svaku lepinju i porciju — biraj priloge po ukusu."
        />

        <Reveal>
          <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-14" data-testid="menu-photo-strip">
            {PHOTOS.map((p, i) => (
              <motion.div
                key={p.label}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", bounce: 0.4, duration: 0.5 }}
                className={`group relative overflow-hidden rounded-[1.25rem] sm:rounded-[1.75rem] border-2 ${
                  i % 2 ? "border-leaf/30" : "border-ember/20"
                } ${i === 1 ? "sm:-translate-y-4" : ""}`}
                data-testid={`menu-photo-${i}`}
              >
                <img
                  src={p.src}
                  alt={p.label}
                  className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <span className="absolute bottom-2.5 left-2.5 sm:bottom-4 sm:left-4 bg-white/92 backdrop-blur rounded-full px-3 py-1.5 font-mono-x text-[9px] sm:text-[10px] uppercase tracking-[0.15em] text-leafdark">
                  {p.label}
                </span>
              </motion.div>
            ))}
          </div>
        </Reveal>

        <Reveal>
          <div className="flex flex-wrap items-center gap-2.5 mb-4" role="tablist">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={active === c.id}
                data-testid={`menu-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`relative rounded-full px-5 sm:px-7 py-3 font-mono-x text-[11px] sm:text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  active === c.id
                    ? "text-white"
                    : "text-leafdark hover:text-ember border border-leaf/30 hover:border-ember/50"
                }`}
              >
                {active === c.id && (
                  <motion.span
                    layoutId="menu-tab-pill"
                    className="absolute inset-0 bg-ember rounded-full"
                    transition={{ type: "spring", bounce: 0.25, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">{c.label}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="inline-flex items-center gap-2 font-mono-x text-[11px] uppercase tracking-[0.15em] text-leafdark bg-leaf/10 border border-leaf/25 rounded-full px-4 py-2 mb-10">
            Porcija = meso + pomfrit + 3 priloga + lepinja
          </p>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="grid md:grid-cols-2 gap-x-14"
            data-testid="menu-items-grid"
          >
            {MENU[active].map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.035, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group py-5 border-b border-leaf/15 hover:border-ember/45 transition-colors duration-300"
              >
                <div className="flex items-baseline">
                  <h3 className="font-display font-bold text-sm sm:text-base uppercase tracking-wide text-leafdark group-hover:text-chili transition-colors duration-300">
                    {item.name}
                  </h3>
                  <span className="price-leader" />
                  <span className="font-mono-x font-bold text-base sm:text-lg text-ember whitespace-nowrap">
                    {item.price} RSD
                  </span>
                </div>
                <p className="text-leafdark/75 text-sm mt-1.5">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        <Reveal delay={0.1}>
          <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-leafdark/70 mt-12 text-center">
            * Savet kuće: uzmi porciju — kompletan obrok po povoljnijoj ceni
          </p>
        </Reveal>
      </div>
    </section>
  );
};
